import { ITokenSale, ITokenSaleTableColumns } from '@entities/trades/token-sell';
import ReduceAndCopyString from '@features/reduce-and-copy-string';
import { ITableActionsConfig, TableActionBtns } from '@features/table-action-btns';
import { FilterDateRange, FilterInput, FilterSelect } from '@features/table-filter';
import { getSelectOptions } from '@shared/components/Form';
import RequestStatusLabel from '@shared/components/RequestStatusLabel';

import { ApproveTokenSale } from '../components/ApproveTokenSale';
import { DownloadTokenSale } from '../components/DownloadTokenSale';
import { RejectTokenSale } from '../components/RejectTokenSale';

interface IProps {
  refetch?: () => void;
}

export const useTableSettings = ({ refetch }: IProps) => {
  // THead settings
  const columnTitles: ITokenSaleTableColumns = {
    id: 'Token Sale ID',
    trader_wallet_id: 'Trader Wallet ID',
    trader_id: 'Trader ID',
    customer_transaction_id: 'UTR',
    external_order_id: 'External Order ID',
    amount: 'Amount',
    created_at: 'Created At',
    updated_at: 'Updated At',
    status: 'Status',
    dispute_file_url: 'Dispute File',
    actions: 'Actions',
  };

  // filter
  const filterStatuses = ['new', 'pending', 'success', 'failed', 'disputed'];
  const renderFilterElement = (key: keyof ITokenSale) => {
    switch (key) {
      case 'status':
        return <FilterSelect name={key} options={getSelectOptions(filterStatuses)} />;

      case 'created_at':
      case 'updated_at':
        return <FilterDateRange name={key} />;

      case 'actions':
      case 'dispute_file_url':
        return null;

      default:
        return <FilterInput name={key} />;
    }
  };

  // TBody settings
  const renderRow = (key: keyof ITokenSaleTableColumns, tokenSale: ITokenSale) => {
    switch (key) {
      case 'status':
        return <RequestStatusLabel status={String(tokenSale[key])} />;

      case 'customer_transaction_id':
        return (
          <ReduceAndCopyString
            navigateTo={`/transactions/deposit?filter[utr]=${tokenSale[key]}`}
            text={tokenSale[key]}
            allowedNavigateRoles={['admin', 'operator']}
          />
        );

      case 'external_order_id':
        return (
          <ReduceAndCopyString
            navigateTo={`/orders/deposit?filter[id]=${tokenSale[key]}`}
            text={tokenSale[key]}
            allowedNavigateRoles={['admin', 'operator']}
          />
        );

      case 'dispute_file_url':
        return <DownloadTokenSale tokenSale={tokenSale} />;

      case 'actions': {
        const actionBtnsConfig: ITableActionsConfig = {
          approve: {
            isEnabled: tokenSale.status === 'disputed',
            isVisible: true,
            popupElem: <ApproveTokenSale tokenSale={tokenSale} refetch={refetch} />,
            popupSize: 'sm',
            allowedRoles: ['admin', 'operator'],
          },
          reject: {
            isEnabled: tokenSale.status === 'disputed',
            isVisible: true,
            popupElem: <RejectTokenSale tokenSale={tokenSale} refetch={refetch} />,
            popupSize: 'sm',
            allowedRoles: ['admin', 'operator'],
          },
        };

        return <TableActionBtns config={actionBtnsConfig} />;
      }

      default:
        return tokenSale[key];
    }
  };

  return {
    columnTitles,
    renderRow,
    renderFilterElement,
  };
};
